import { useLocation } from 'react-router'
import { BreadcrumbsSkeleton } from '@/components/layout/breadcrumbs/breadcrumbs-skeleton'
import { CardSkeleton } from '@/components/layout/card-skeleton'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
  SidebarTrigger,
} from '@/components/sv/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { PipelineBoardFallback } from '@/routes/pipeline/pipeline-board/fallback'

export const LayoutSkeleton = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const location = useLocation()

  const Fallback = () => {
    if (location.pathname.startsWith('/pipeline')) {
      return <PipelineBoardFallback />
    }
    return <CardSkeleton />
  }

  return (
    <div data-layout="root" className="flex w-full min-h-screen bg-sidebar">
      <Sidebar collapsible="icon">
        <SidebarHeader className="h-[3.25rem] border-b border-sidebar-border flex-row items-center pl-2.5">
          <Skeleton className="h-7 w-[5.5rem] group-data-[collapsible=icon]:w-7" />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {Array.from({ length: 8 }).map((_, index) => (
                  <SidebarMenuItem key={index}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            {Array.from({ length: 4 }).map((_, index) => (
              <SidebarMenuItem key={index}>
                <SidebarMenuSkeleton showIcon />
              </SidebarMenuItem>
            ))}
            <SidebarMenuItem>
              <div className="flex items-center gap-2 p-2">
                <Skeleton className="size-8 rounded-lg shrink-0" />
                <div className="grid flex-1 gap-1 group-data-[collapsible=icon]:hidden">
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <main className="flex-1 min-w-0 flex flex-col bg-background">
        <header className="sticky z-20 top-0 bg-background flex items-center gap-2 px-4 h-[3.25rem] border-b border-border">
          <SidebarTrigger className="-ml-1" />
          <BreadcrumbsSkeleton />
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="size-8 rounded-md" />
          </div>
        </header>
        <div className="flex-1 p-4">
          <div className="w-full max-w-[1280px] mx-auto">
            {children ?? <Fallback />}
          </div>
        </div>
      </main>
    </div>
  )
}
